import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Stethoscope, MessageSquare } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useAuthStore } from "@/store/authStore";

function timeAgo(iso) {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "now";
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

export default function PharmacyInbox() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const userId = user?._id || user?.id || user?.userId;
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!userId) { setLoading(false); return; }
    try {
      const res = await api.get(`/pharmacy-chat/patient/${userId}`);
      if (res.data?.success) setConversations(res.data.conversations || []);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="max-w-2xl mx-auto px-4 pb-24">
      <div className="flex items-center gap-2 py-4 sticky top-0 bg-background z-10">
        <button onClick={() => navigate(-1)} data-testid="inbox-back" className="h-10 w-10 flex items-center justify-center rounded-full hover:bg-accent"><ArrowLeft className="h-5 w-5" /></button>
        <h1 className="text-2xl font-semibold">Pharmacy Enquiries</h1>
      </div>

      {loading ? (
        <p className="text-center text-muted-foreground mt-10">Loading…</p>
      ) : conversations.length === 0 ? (
        <Card className="rounded-3xl p-8 text-center">
          <MessageSquare className="h-10 w-10 mx-auto text-muted-foreground/40 mb-3" />
          <p className="text-muted-foreground mb-4">No enquiries yet. Open a pharmacy from Find Pharmacies and tap &quot;Enquire&quot; to ask about medicine availability.</p>
          <Button onClick={() => navigate("/find-pharmacies")} data-testid="inbox-find-pharmacies" style={{ backgroundColor: "#005C66" }} className="rounded-xl">Find Pharmacies</Button>
        </Card>
      ) : (
        <div className="space-y-3">
          {conversations.map((c) => (
            <Card
              key={c.id}
              data-testid={`inbox-conv-${c.id}`}
              onClick={() => navigate(`/pharmacy-chat?conversationId=${c.id}&pharmacyName=${encodeURIComponent(c.pharmacyName || "")}&pharmacyId=${c.pharmacyId}`)}
              className="rounded-2xl p-4 flex items-center gap-3 cursor-pointer hover:shadow-md transition-all"
            >
              <div className="h-11 w-11 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: "#E0F2F1" }}><Stethoscope className="h-5 w-5" style={{ color: "#005C66" }} /></div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between">
                  <p className="font-semibold truncate">{c.pharmacyName || "Pharmacy"}</p>
                  <span className="text-xs text-muted-foreground ml-2">{timeAgo(c.lastMessageAt)}</span>
                </div>
                <div className="flex items-center">
                  <p className={`text-sm truncate flex-1 ${c.unreadPatient > 0 ? "font-semibold text-foreground" : "text-muted-foreground"}`}>
                    {c.lastSender === "patient" ? "You: " : ""}{c.lastMessage || "Start a conversation"}
                  </p>
                  {c.unreadPatient > 0 && <span className="ml-2 text-xs text-white rounded-full px-2 py-0.5" style={{ backgroundColor: "#005C66" }}>{c.unreadPatient}</span>}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
